import { useEffect, useState } from 'react';
import { Icona } from '../../components/Icona';
import { commesse } from '../../api/commesse';
import type { CommessaAperta } from '../../api/commesse';
import { subappalti } from '../../api/subappalti';
import type { SubappaltoDaAffidare } from '../../api/subappalti';
import { formattaImporto } from '../../lib/formato';
import { numeroDecimale } from '../../lib/numeri';
import { ScegliFornitore } from './ScegliFornitore';

/*  UN SUBAPPALTO NUOVO. Prima la commessa; poi, se la commessa viene da un preventivo,
    le voci segnate «subappalto» che il cliente ha preso, con quanto si era preventivato
    e quanto e' gia' affidato. Scelta la voce, impresa, descrizione e importo si
    compilano da li' e si correggono. Senza voci — commessa aperta a mano, o un lavoro
    che il preventivo non diceva — si scrive libero.  */
export function NuovoSubappalto({
  onAnnulla,
  onCreato,
}: {
  onAnnulla: () => void;
  onCreato: (id: string) => void;
}) {
  const [aperte, setAperte] = useState<CommessaAperta[] | null>(null);
  const [commessaId, setCommessaId] = useState('');
  const [voci, setVoci] = useState<SubappaltoDaAffidare[] | null>(null);
  const [voceId, setVoceId] = useState<string>('');
  const [fornitore, setFornitore] = useState<{ codice: string; nome: string } | null>(null);
  const [descrizione, setDescrizione] = useState('');
  const [importo, setImporto] = useState('');
  const [inizio, setInizio] = useState('');
  const [fine, setFine] = useState('');
  const [inCorso, setInCorso] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);

  useEffect(() => {
    commesse
      .aperte()
      .then(setAperte)
      .catch((e: Error) => setErrore(e.message));
  }, []);

  useEffect(() => {
    setVoci(null);
    setVoceId('');
    if (commessaId === '') return;
    subappalti
      .daAffidare(commessaId)
      .then(setVoci)
      .catch((e: Error) => setErrore(e.message));
  }, [commessaId]);

  function scegliVoce(id: string) {
    setVoceId(id);
    const v = voci?.find((x) => x.rigaPreventivoId === id);
    if (!v) return;
    setDescrizione(v.descrizione);
    setImporto(String(v.daAffidare > 0 ? v.daAffidare : v.preventivato));
    if (v.codiceFornitoreMago) setFornitore({ codice: v.codiceFornitoreMago, nome: v.fornitore ?? v.codiceFornitoreMago });
  }

  const valore = numeroDecimale(importo);
  const pronto = commessaId !== '' && fornitore !== null && descrizione.trim() !== '' && valore !== null && valore >= 0;

  async function crea() {
    if (!pronto || fornitore === null) return;
    setInCorso(true);
    setErrore(null);
    try {
      const nato = await subappalti.crea({
        commessaId,
        rigaPreventivoId: voceId === '' ? null : voceId,
        codiceFornitoreMago: fornitore.codice,
        fornitore: fornitore.nome,
        descrizione: descrizione.trim(),
        importo: valore ?? 0,
        dataInizio: inizio === '' ? null : inizio,
        dataFine: fine === '' ? null : fine,
      });
      onCreato(nato.id);
    } catch (e) {
      setErrore((e as Error).message);
      setInCorso(false);
    }
  }

  return (
    <aside className="scheda nuovo-subappalto" aria-label="Nuovo subappalto">
      <header>
        <div>
          <h2>Nuovo subappalto</h2>
        </div>
        <button type="button" className="chiudi" onClick={onAnnulla} aria-label="Chiudi">
          <Icona nome="chiudi" />
        </button>
      </header>

      <label className="campo-blocco">
        <span>Commessa</span>
        <select value={commessaId} onChange={(e) => setCommessaId(e.target.value)}>
          <option value="">{aperte === null ? 'Leggo le commesse…' : 'Scegli la commessa'}</option>
          {(aperte ?? []).map((c) => (
            <option key={c.id} value={c.id}>
              {c.numero} — {c.descrizione}
              {c.cliente ? ` (${c.cliente})` : ''}
            </option>
          ))}
        </select>
      </label>

      {commessaId !== '' && voci !== null && (
        voci.length === 0 ? (
          <p className="nota">
            Il preventivo di questa commessa non ha voci segnate «subappalto» (o la commessa è
            aperta a mano): il subappalto si scrive libero.
          </p>
        ) : (
          <fieldset className="voci-subappalto">
            <legend>Dal preventivo</legend>
            {voci.map((v) => (
              <label key={v.rigaPreventivoId} className={v.daAffidare === 0 ? 'affidata' : ''}>
                <input
                  type="radio"
                  name="voce"
                  checked={voceId === v.rigaPreventivoId}
                  onChange={() => scegliVoce(v.rigaPreventivoId)}
                />
                <span>
                  <strong>{v.descrizione}</strong>
                  {v.capitolo && <em className="dettaglio"> {v.capitolo}</em>}
                  <span className="dettaglio">
                    {' '}
                    preventivati {formattaImporto(v.preventivato)}
                    {v.giaAffidato > 0 && <> · già affidati {formattaImporto(v.giaAffidato)}</>}
                    {v.fornitore && <> · {v.fornitore}</>}
                  </span>
                </span>
              </label>
            ))}
            <label>
              <input type="radio" name="voce" checked={voceId === ''} onChange={() => setVoceId('')} />
              <span>Lavoro non previsto nel preventivo: scritto libero</span>
            </label>
          </fieldset>
        )
      )}

      <div className="campo-blocco">
        <span>Impresa</span>
        {fornitore ? (
          <p>
            <strong>{fornitore.nome}</strong> <em className="chiave">{fornitore.codice}</em>{' '}
            <button type="button" className="collegamento" onClick={() => setFornitore(null)}>
              cambia
            </button>
          </p>
        ) : (
          <ScegliFornitore valore={null} onScelto={(codice, nome) => setFornitore({ codice, nome })} />
        )}
      </div>

      <label className="campo-blocco">
        <span>Lavoro affidato</span>
        <textarea rows={3} value={descrizione} onChange={(e) => setDescrizione(e.target.value)} />
      </label>

      <div className="campi">
        <label className="campo-blocco">
          <span>Importo</span>
          <input inputMode="decimal" value={importo} onChange={(e) => setImporto(e.target.value)} />
        </label>
        <label className="campo-blocco">
          <span>Dal</span>
          <input type="date" value={inizio} onChange={(e) => setInizio(e.target.value)} />
        </label>
        <label className="campo-blocco">
          <span>Al</span>
          <input type="date" value={fine} onChange={(e) => setFine(e.target.value)} />
        </label>
      </div>

      {errore && <p className="errore">{errore}</p>}

      <div className="azioni">
        <button type="button" className="primario" disabled={!pronto || inCorso} onClick={() => void crea()}>
          Apri il subappalto
        </button>
        <button type="button" disabled={inCorso} onClick={onAnnulla}>
          Annulla
        </button>
      </div>
    </aside>
  );
}
